import React, {
    useState,
    useMemo,
    useEffect,
    useCallback,
    type CSSProperties,
} from "react";
import { ScannerData } from "../types/ScannerData";
import {
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ComposedChart,
    ReferenceLine,
    Cell,
} from "recharts";
import { useLanguage } from "../contexts/LanguageContext";
import { useTheme } from "../contexts/ThemeContext";
import { useSymbolMapping } from "../contexts/SymbolMappingContext";
import { processOptionsData, getMappedSymbol } from "../utils/optionsUtils";
import { formatDate } from "../utils/dateUtils";
import { useMmecFx } from "../theme/mmecFx";
import { useChartTheme } from "../theme/chartTheme";
import {
    collectDistinctMappedUnderlyings,
    getFilterOptions,
} from "../utils/filterUtils";
import ToolExplanation from "./ToolExplanation";
import { isDesignSystemOn } from "../theme/mmecDsPreview";
import ToolState from "./mmec/ToolState";
import "./VolumeDeltaReformed.ds.css";
import StandardFilterBar from "./StandardFilterBar";
import { usePersistentFilters } from "../utils/persistentFilters";
import { useHistoricalData } from "../hooks/useHistoricalData";
import { HistoricalDataSelector } from "./HistoricalDataSelector";
import { useHistoricalModal } from "../contexts/HistoricalModalContext";
import { filterLegacyVolumeScannerData } from "../utils/legacyVolumeChartFilters";
import {
    aggregateLegacyVolumeByStrike,
    filterStrikesByWindow,
} from "../utils/legacyVolumeStrikeAggregation";

interface VolumeDeltaReformedProps {
    data: ScannerData[];
    fullScreen?: boolean;
    hideFilters?: boolean;
    showDataModeFilter?: boolean;
    dataMode?: "accumulated" | "5min";
    showGrid?: boolean;
    showLegend?: boolean;
    hideSummaryStats?: boolean;
    prefilteredScannerRows?: ScannerData[] | null;
    persistentFilterKey?: string;
}

const VolumeDeltaReformed: React.FC<VolumeDeltaReformedProps> = ({
    data,
    fullScreen = false,
    hideFilters = false,
    showDataModeFilter = true,
    dataMode: propDataMode,
    showGrid = true,
    showLegend = true,
    hideSummaryStats = false,
    prefilteredScannerRows = null,
    persistentFilterKey,
}) => {
    const { colors, isDarkMode } = useTheme();
    const fx = useMmecFx();
    const ct = useChartTheme();
    /** Design System preview flag: chart colours as tokens.css hexes (Recharts props cannot read CSS vars). */
    const ds = isDesignSystemOn();
    const chartUp = ds ? "#26a69a" : colors.chartSuccess;
    const chartDown = ds ? "#ef5350" : colors.chartError;
    const priceLine = ds ? "#b2b5be" : colors.accent;
    const { language } = useLanguage();
    const { isSymbolMappingEnabled } = useSymbolMapping();

    // Historical data support
    const {
        selectedDate,
        fetchHistoricalData,
        isHistoricalMode,
    } = useHistoricalData();

    // State for historical data
    const [historicalData, setHistoricalData] = useState<ScannerData[]>([]);
    const [, setIsLoadingHistorical] = useState(false);

    // Effect to fetch historical data when date changes
    useEffect(() => {
        if (selectedDate) {
            setIsLoadingHistorical(true);
            fetchHistoricalData(selectedDate).then((data) => {
                if (data) {
                    setHistoricalData(data);
                }
                setIsLoadingHistorical(false);
            });
        } else {
            setHistoricalData([]);
        }
    }, [selectedDate, fetchHistoricalData]);

    // Use historical data if in historical mode, otherwise use current data
    const effectiveData = isHistoricalMode ? historicalData : data;

    // Use persistent filters instead of global data mode
    const {
        selectedUnderlying,
        selectedExpiry,
        selectedSide,
        selectedMoneyness,
        selectedDataMode,
        setSelectedUnderlying,
        setSelectedExpiry,
        setSelectedSide,
        setSelectedMoneyness,
        setSelectedDataMode,
    } = usePersistentFilters(
        persistentFilterKey ?? "VolumeDeltaReformed",
        propDataMode || "5min",
    );

    const effectiveDataMode = hideFilters
        ? propDataMode || selectedDataMode
        : selectedDataMode;

    useEffect(() => {
        const onGlobalMode = (e: Event) => {
            const mode = (e as CustomEvent<{ mode?: "accumulated" | "5min" }>)
                .detail?.mode;
            if (mode === "accumulated" || mode === "5min") {
                setSelectedDataMode(mode);
            }
        };
        window.addEventListener("mmecDataModeChanged", onGlobalMode);
        return () =>
            window.removeEventListener("mmecDataModeChanged", onGlobalMode);
    }, [setSelectedDataMode]);

    const { openHistorical } = useHistoricalModal();
    const handleStrikeClick = (payload: { strike?: string | number }) => {
        if (payload?.strike == null || !selectedUnderlying) return;
        const optionType =
            selectedSide === "put" || selectedSide === "P" ? "PUT" : "CALL";
        openHistorical({
            underlying: selectedUnderlying,
            strike: Number(payload.strike) || payload.strike,
            expiry: selectedExpiry || undefined,
            optionType,
            source: "delta",
        });
    };
    const onBarClick = (data: unknown) => {
        const payload =
            (data as { payload?: { strike?: string | number } })?.payload ??
            (data as { strike?: string | number });
        handleStrikeClick(payload);
    };

    // Category filter state
    const [selectedCategory, setSelectedCategory] = useState<
        "all" | "stocks" | "etfs" | "indices"
    >("all");

    const [] = useState(false);

    // Track if filters have been validated on mount (prevent reset on data refresh)
    const filtersValidatedRef = React.useRef(false);

    // Strikes filter state
    const [selectedStrikes, setSelectedStrikes] = useState<
        "10" | "20" | "30" | "all" | "custom"
    >("all");
    const [customStrikeFrom, setCustomStrikeFrom] = useState<string>("");
    const [customStrikeTo, setCustomStrikeTo] = useState<string>("");

    // Get filter options from raw data (for sides/moneyness)
    const rawFilterOptions = useMemo(
        () => getFilterOptions(effectiveData as any),
        [effectiveData],
    );
    const availableSides = rawFilterOptions.availableSides;
    const availableMoneyness = rawFilterOptions.availableMoneyness;

    // Translations
    const translations = useMemo(
        () => ({
            title:
                language === "ar"
                    ? "تحليل حجم الدلتا المطور"
                    : "Volume Delta Reformed Analysis",
            subtitle:
                language === "ar"
                    ? "تحليل أحجام البيع والشراء حسب سعر التنفيذ مع صافي الدلتا"
                    : "Buy/Sell volume analysis by strike with net delta",
            boughtCalls:
                language === "ar" ? "خيارات الشراء المشتراة" : "Bought Calls",
            soldCalls:
                language === "ar" ? "خيارات الشراء المباعة" : "Sold Calls",
            boughtPuts:
                language === "ar" ? "خيارات البيع المشتراة" : "Bought Puts",
            soldPuts: language === "ar" ? "خيارات البيع المباعة" : "Sold Puts",
            strike: language === "ar" ? "سعر التنفيذ" : "Strike",
            volume: language === "ar" ? "الحجم" : "Volume",
            netDelta: language === "ar" ? "صافي الدلتا" : "Net Delta",
            currentPrice: language === "ar" ? "السعر الحالي" : "Current Price",
            allUnderlyings:
                language === "ar" ? "جميع الأصول الأساسية" : "All Underlyings",
            allExpiries:
                language === "ar" ? "جميع انتهاء الصلاحية" : "All Expiries",
            noData:
                language === "ar"
                    ? "لا توجد بيانات متاحة"
                    : "No data available",
            bullish: language === "ar" ? "صاعد" : "Bullish",
            bearish: language === "ar" ? "هابط" : "Bearish",
            neutral: language === "ar" ? "محايد" : "Neutral",
            sentiment: language === "ar" ? "المشاعر" : "Sentiment",
            strikes: language === "ar" ? "عدد سعر التنفيذ" : "Strikes",
            strikes10: language === "ar" ? "10 سعر التنفيذ" : "10 Strikes",
            strikes20: language === "ar" ? "20 سعر التنفيذ" : "20 Strikes",
            strikes30: language === "ar" ? "30 سعر التنفيذ" : "30 Strikes",
            strikesAll: language === "ar" ? "جميع سعر التنفيذ" : "All Strikes",
        }),
        [language],
    );

    const selectFieldStyle = useMemo(
        (): CSSProperties => ({
            width: "100%",
            padding: "8px 12px",
            borderRadius: 6,
            border: `1px solid ${colors.surfaceBorder}`,
            background: colors.surfaceHover,
            color: colors.text,
            fontSize: 14,
            colorScheme: isDarkMode ? "dark" : "light",
        }),
        [colors.surfaceBorder, colors.surfaceHover, colors.text, isDarkMode],
    );

    const compactSelectStyle = useMemo(
        (): CSSProperties => ({
            borderRadius: 4,
            border: `1px solid ${colors.surfaceBorder}`,
            backgroundColor: colors.surface,
            color: colors.text,
            fontSize: 11,
            padding: "4px 8px",
            colorScheme: isDarkMode ? "dark" : "light",
        }),
        [colors.surfaceBorder, colors.surface, colors.text, isDarkMode],
    );

    const reformedColors = useMemo(
        () => ({
            // DS: colour by bias (D1) — bought calls + sold puts up, bought puts + sold calls down; puts lighter.
            boughtCalls: ds ? "#26a69a" : colors.boughtCalls,
            soldPuts: ds ? "rgba(38,166,154,0.5)" : colors.secondary,
            boughtPuts: ds ? "rgba(239,83,80,0.5)" : colors.boughtPuts,
            soldCalls: ds ? "#ef5350" : colors.soldCalls,
        }),
        [colors, ds],
    );

    const filteredData = useMemo(() => {
        if (hideFilters && prefilteredScannerRows != null) {
            return prefilteredScannerRows;
        }
        if (hideFilters) {
            return data;
        }
        const raw = filterLegacyVolumeScannerData(effectiveData, {
            selectedCategory,
            selectedUnderlying,
            selectedExpiry,
            selectedSide,
            selectedMoneyness,
            selectedDataMode: effectiveDataMode,
            isSymbolMappingEnabled,
        });
        return processOptionsData(raw as ScannerData[], effectiveDataMode);
    }, [
        data,
        effectiveData,
        hideFilters,
        prefilteredScannerRows,
        selectedCategory,
        selectedUnderlying,
        selectedExpiry,
        selectedSide,
        selectedMoneyness,
        effectiveDataMode,
        isSymbolMappingEnabled,
    ]);

    // Get available underlyings and expiries for filters
    const underlyings = useMemo(() => {
        const underlyingSet = new Set<string>();
        effectiveData.forEach((item) => {
            const originalUnderlying = item.Underlying || "";
            const mappedUnderlying = getMappedSymbol(
                originalUnderlying,
                isSymbolMappingEnabled,
            );
            underlyingSet.add(mappedUnderlying);
        });
        return Array.from(underlyingSet).sort();
    }, [effectiveData, isSymbolMappingEnabled]);

    const availableExpiries = useMemo(() => {
        const expiries = new Set<string>();
        effectiveData.forEach((item) => {
            if (item["Expiry Date"]) {
                const rawExpiry = item["Expiry Date"];
                const formattedExpiry = formatDate(rawExpiry);
                expiries.add(formattedExpiry);
            }
        });
        return Array.from(expiries).sort();
    }, [effectiveData]);

    // Guard: reset invalid persisted selections with better error handling
    // Validate filters on mount and when options change
    useEffect(() => {
        let needsReset = false;

        if (
            selectedUnderlying !== "all" &&
            underlyings.length > 0 &&
            !underlyings.includes(selectedUnderlying)
        ) {
            console.warn(
                `⚠️ [VolumeDeltaReformed] Invalid underlying "${selectedUnderlying}", resetting to "all"`,
            );
            setSelectedUnderlying("all");
            needsReset = true;
        }
        if (
            selectedExpiry !== "all" &&
            availableExpiries.length > 0 &&
            !availableExpiries.includes(selectedExpiry)
        ) {
            console.warn(
                `⚠️ [VolumeDeltaReformed] Invalid expiry "${selectedExpiry}", resetting to "all"`,
            );
            setSelectedExpiry("all");
            needsReset = true;
        }
        if (
            selectedSide !== "all" &&
            availableSides &&
            availableSides.length > 0 &&
            !(availableSides || [])
                .map((s) => String(s).toUpperCase())
                .includes(String(selectedSide).toUpperCase())
        ) {
            console.warn(
                `⚠️ [VolumeDeltaReformed] Invalid side "${selectedSide}", resetting to "all"`,
            );
            setSelectedSide("all");
            needsReset = true;
        }
        if (
            selectedMoneyness !== "all" &&
            availableMoneyness &&
            availableMoneyness.length > 0 &&
            !(availableMoneyness || [])
                .map(String)
                .includes(String(selectedMoneyness))
        ) {
            console.warn(
                `⚠️ [VolumeDeltaReformed] Invalid moneyness "${selectedMoneyness}", resetting to "all"`,
            );
            setSelectedMoneyness("all");
            needsReset = true;
        }

        // Mark as validated only if we didn't need to reset (prevents infinite loops)
        if (!needsReset) {
            filtersValidatedRef.current = true;
        }
    }, [
        underlyings,
        availableExpiries,
        availableSides,
        availableMoneyness,
        selectedUnderlying,
        selectedExpiry,
        selectedSide,
        selectedMoneyness,
        setSelectedUnderlying,
        setSelectedExpiry,
        setSelectedSide,
        setSelectedMoneyness,
    ]);

    // Reset function to reset all filters to safe defaults
    const resetFilters = useCallback(() => {
        setSelectedUnderlying("all");
        setSelectedExpiry("all");
        setSelectedSide("all");
        setSelectedMoneyness("all");
        setSelectedStrikes("all");
        setCustomStrikeFrom("");
        setCustomStrikeTo("");
        filtersValidatedRef.current = false; // Allow re-validation
    }, [
        setSelectedUnderlying,
        setSelectedExpiry,
        setSelectedSide,
        setSelectedMoneyness,
    ]);

    const chartData = useMemo(() => {
        if (filteredData.length === 0) return [];
        const agg = aggregateLegacyVolumeByStrike(
            filteredData,
            effectiveDataMode,
            isSymbolMappingEnabled,
        );
        const mapped = agg.map((row) => ({
            strike: row.strike,
            boughtCalls: row.boughtCalls,
            soldCalls: row.soldCalls,
            boughtPuts: row.boughtPuts,
            soldPuts: row.soldPuts,
            netDelta: row.netDirectionalVolume,
            netPremium: row.netPremium,
            underlyingPrice: row.underlyingPrice,
            underlyingSymbol: row.underlyingSymbol,
        }));
        return filterStrikesByWindow(
            mapped,
            selectedStrikes,
            customStrikeFrom,
            customStrikeTo,
        );
    }, [
        filteredData,
        effectiveDataMode,
        isSymbolMappingEnabled,
        selectedStrikes,
    ]);

    // Custom tooltip component
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0]?.payload;
            const sentiment =
                data?.netDelta > 0
                    ? translations.bullish
                    : data?.netDelta < 0
                      ? translations.bearish
                      : translations.neutral;
            const uSym = data?.underlyingSymbol;
            const showUnderlying =
                selectedUnderlying === "all" &&
                typeof uSym === "string" &&
                uSym.trim().length > 0 &&
                uSym !== "Unknown";

            return (
                <div
                    className="text-sm rounded-lg shadow-lg"
                    style={{
                        ...fx.tooltipCard,
                        borderColor: colors.surfaceBorder,
                    }}
                >
                    {showUnderlying && (
                        <p
                            className="font-semibold text-[13px] mb-2"
                            style={{ color: colors.text }}
                        >
                            {uSym}
                        </p>
                    )}
                    <p className="font-semibold" style={{ color: colors.text }}>
                        {translations.strike}: {data?.strike || label}
                    </p>

                    {payload.map((entry: any, index: number) => (
                        <p key={index} style={{ color: entry.color }}>
                            {entry.name}:{" "}
                            {typeof entry.value === "number"
                                ? entry.value.toLocaleString()
                                : entry.value}
                        </p>
                    ))}
                    {data?.netDelta !== undefined && (
                        <p style={{ color: colors.textSecondary }}>
                            {translations.netDelta}:{" "}
                            {data.netDelta?.toFixed(0) || 0}
                        </p>
                    )}
                    {data?.netPremium !== undefined && (
                        <p style={{ color: colors.textSecondary }}>
                            {language === "ar" ? "صافي العلاوة" : "Net Premium"}
                            : $
                            {typeof data.netPremium === "number"
                                ? data.netPremium.toLocaleString(undefined, {
                                      maximumFractionDigits: 0,
                                  })
                                : "0"}
                        </p>
                    )}
                    <p style={{ color: colors.textSecondary }}>
                        {translations.sentiment}: {sentiment}
                    </p>
                </div>
            );
        }
        return null;
    };

    /** When filter is All, use full filtered rows so labels match dropdowns even if strike rows omit symbol. */
    const uniqueUnderlyings = useMemo(
        () =>
            selectedUnderlying === "all"
                ? collectDistinctMappedUnderlyings(
                      filteredData,
                      isSymbolMappingEnabled,
                  )
                : [],
        [filteredData, isSymbolMappingEnabled, selectedUnderlying],
    );

    // Custom legend — series swatches only; underlying is shown above the chart when filter is All
    const CustomLegend = (props: any) => {
        const { payload } = props;

        return (
            <div
                className="flex flex-wrap items-center gap-4 justify-center"
                style={{ color: colors.text, fontSize: 12 }}
            >
                {payload &&
                    payload.map((entry: any, index: number) => (
                        <div key={index} className="flex items-center gap-2">
                            <div
                                className="w-3 h-3 rounded"
                                style={{ backgroundColor: entry.color }}
                            />
                            <span>{entry.value}</span>
                        </div>
                    ))}
            </div>
        );
    };

    // Calculate summary statistics
    const summaryStats = useMemo(() => {
        if (chartData.length === 0) return null;

        const totalBoughtCalls = chartData.reduce(
            (sum, item) => sum + item.boughtCalls,
            0,
        );
        const totalSoldCalls = chartData.reduce(
            (sum, item) => sum + item.soldCalls,
            0,
        );
        const totalBoughtPuts = chartData.reduce(
            (sum, item) => sum + item.boughtPuts,
            0,
        );
        const totalSoldPuts = chartData.reduce(
            (sum, item) => sum + item.soldPuts,
            0,
        );
        const totalNetDelta = chartData.reduce(
            (sum, item) => sum + item.netDelta,
            0,
        );
        const bullishStrikes = chartData.filter((d) => d.netDelta > 0).length;
        const bearishStrikes = chartData.filter((d) => d.netDelta < 0).length;
        const neutralStrikes = chartData.filter((d) => d.netDelta === 0).length;

        return {
            totalBoughtCalls,
            totalSoldCalls,
            totalBoughtPuts,
            totalSoldPuts,
            totalNetDelta,
            bullishStrikes,
            bearishStrikes,
            neutralStrikes,
            dataPoints: chartData.length,
        };
    }, [chartData]);

    // Get current underlying price for reference line
    const currentPrice = useMemo(() => {
        if (chartData.length === 0) return null;
        const prices = chartData
            .map((item) => item.underlyingPrice)
            .filter((price) => price > 0);
        const underlyingPrice = prices.length > 0 ? prices[0] : null;

        if (!underlyingPrice) return null;

        // Find the closest strike to the underlying price
        const strikes = chartData.map((item) => parseFloat(item.strike));
        const closestStrike = strikes.reduce((closest, strike) => {
            return Math.abs(strike - underlyingPrice) <
                Math.abs(closest - underlyingPrice)
                ? strike
                : closest;
        });
        return closestStrike.toString();
    }, [chartData]);

    // No data state with reset option
    if (chartData.length === 0) {
        return (
            <div
                className={`rounded-xl shadow-lg p-6 transition-all duration-300 hover:shadow-xl${ds ? " vdr-ds mmec" : ""}`}
                style={{
                    color: colors.text,
                    border: `1px solid ${colors.surfaceBorder}`,
                    background: colors.surface,
                }}
            >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                    <div>
                        <h2
                            className="text-[18px] font-semibold mb-1"
                            style={{ color: colors.text }}
                        >
                            {translations.title}
                        </h2>
                        <p
                            className="text-sm"
                            style={{ color: colors.textMuted }}
                        >
                            {translations.subtitle}
                        </p>
                    </div>

                    {!hideFilters && (
                        <div className="mb-4">
                            <HistoricalDataSelector isDarkMode={isDarkMode} />
                        </div>
                    )}
                </div>

                {!hideFilters && (
                    <div className="mb-4">
                        <div className="mb-3">
                            <label
                                className="block text-xs font-medium mb-1"
                                style={{ color: colors.textSecondary }}
                            >
                                {language === "ar" ? "الفئة" : "Category"}
                            </label>
                            <select
                                value={selectedCategory}
                                onChange={(e) =>
                                    setSelectedCategory(
                                        e.target.value as
                                            | "all"
                                            | "stocks"
                                            | "etfs"
                                            | "indices",
                                    )
                                }
                                style={{ ...compactSelectStyle, width: 128 }}
                            >
                                <option value="all">
                                    {language === "ar" ? "الكل" : "All"}
                                </option>
                                <option value="stocks">
                                    {language === "ar" ? "الأسهم" : "Stocks"}
                                </option>
                                <option value="etfs">
                                    {language === "ar"
                                        ? "صناديق الاستثمار"
                                        : "ETFs"}
                                </option>
                                <option value="indices">
                                    {language === "ar" ? "المؤشرات" : "Indices"}
                                </option>
                            </select>
                        </div>

                        <StandardFilterBar
                            selectedUnderlying={selectedUnderlying}
                            selectedExpiry={selectedExpiry}
                            selectedSide={selectedSide}
                            selectedMoneyness={selectedMoneyness}
                            selectedDataMode={selectedDataMode}
                            availableUnderlyings={underlyings}
                            availableExpiries={availableExpiries}
                            availableSides={availableSides}
                            availableMoneyness={availableMoneyness}
                            onUnderlyingChange={setSelectedUnderlying}
                            onExpiryChange={setSelectedExpiry}
                            onSideChange={setSelectedSide}
                            onMoneynessChange={setSelectedMoneyness}
                            onDataModeChange={(mode) => {
                                setSelectedDataMode(mode);
                                window.dispatchEvent(
                                    new CustomEvent("mmecDataModeChanged", {
                                        detail: { mode },
                                    }),
                                );
                            }}
                            showDataModeFilter={showDataModeFilter}
                            compact={false}
                        />
                    </div>
                )}

                {ds ? (
                    <ToolState
                        kind="empty"
                        lang={language === "ar" ? "ar" : "en"}
                        title={translations.noData}
                        body={
                            filteredData.length === 0
                                ? language === "ar"
                                    ? "لا توجد بيانات متطابقة مع المرشحات المحددة."
                                    : "No data matches the selected filters."
                                : language === "ar"
                                  ? "تمت تصفية البيانات ولكن لا توجد بيانات صالحة للعرض."
                                  : "Data filtered but no valid data to display."
                        }
                        onAction={filteredData.length === 0 && !hideFilters ? resetFilters : undefined}
                        actionLabel={language === "ar" ? "إعادة تعيين المرشحات" : "Reset Filters"}
                    />
                ) : (
                <div className="text-center py-12">
                    <p
                        className="text-[13px] mb-4"
                        style={{ color: colors.textMuted }}
                    >
                        {translations.noData}
                    </p>
                    {filteredData.length === 0 && (
                        <div className="space-y-2">
                            <p
                                className="text-sm"
                                style={{ color: colors.textMuted }}
                            >
                                {language === "ar"
                                    ? "لا توجد بيانات متطابقة مع المرشحات المحددة."
                                    : "No data matches the selected filters."}
                            </p>
                            {!hideFilters && (
                                <button
                                    type="button"
                                    onClick={resetFilters}
                                    style={fx.btnPrimary}
                                >
                                    {language === "ar"
                                        ? "إعادة تعيين المرشحات"
                                        : "Reset Filters"}
                                </button>
                            )}
                        </div>
                    )}
                    {filteredData.length > 0 && chartData.length === 0 && (
                        <p
                            className="text-sm"
                            style={{ color: colors.textMuted }}
                        >
                            {language === "ar"
                                ? "تمت تصفية البيانات ولكن لا توجد بيانات صالحة للعرض."
                                : "Data filtered but no valid data to display."}
                        </p>
                    )}
                </div>
                )}
            </div>
        );
    }

    return (
        <div
            className={`rounded-xl shadow-lg p-6 transition-all duration-300 hover:shadow-xl relative${ds ? " vdr-ds mmec" : ""}`}
            style={{
                color: colors.text,
                border: `1px solid ${colors.surfaceBorder}`,
                background: colors.surface,
            }}
        >
            <span
                className={`absolute top-4 right-4 text-xs select-all${ds ? " vdr-pageid" : ""}`}
                style={{ padding: "4px 8px", color: colors.textMuted }}
            >
                Page ID: VOLUME_DELTA_REFORMED
            </span>

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                <div>
                    <h2
                        className="text-[18px] font-semibold mb-1"
                        style={{ color: colors.text }}
                    >
                        {translations.title}
                    </h2>
                    <p className="text-sm" style={{ color: colors.textMuted }}>
                        {translations.subtitle}
                    </p>
                </div>

                {!hideFilters && (
                    <div className="mb-4">
                        <HistoricalDataSelector isDarkMode={isDarkMode} />
                    </div>
                )}

                {!hideFilters && (
                    <div className="mt-4 lg:mt-0">
                        <div className="flex items-center justify-between mb-2">
                            <h3
                                className="text-sm font-medium"
                                style={{ color: colors.textSecondary }}
                            >
                                {language === "ar" ? "المرشحات" : "Filters"}
                            </h3>
                            <button
                                type="button"
                                onClick={resetFilters}
                                style={fx.btnGhost}
                                title={
                                    language === "ar"
                                        ? "إعادة تعيين جميع المرشحات"
                                        : "Reset all filters"
                                }
                            >
                                {language === "ar" ? "إعادة تعيين" : "Reset"}
                            </button>
                        </div>
                        <div className="mb-3">
                            <label
                                className="block text-xs font-medium mb-1"
                                style={{ color: colors.textSecondary }}
                            >
                                {language === "ar" ? "الفئة" : "Category"}
                            </label>
                            <select
                                value={selectedCategory}
                                onChange={(e) =>
                                    setSelectedCategory(
                                        e.target.value as
                                            | "all"
                                            | "stocks"
                                            | "etfs"
                                            | "indices",
                                    )
                                }
                                style={{ ...compactSelectStyle, width: 128 }}
                            >
                                <option value="all">
                                    {language === "ar" ? "الكل" : "All"}
                                </option>
                                <option value="stocks">
                                    {language === "ar" ? "الأسهم" : "Stocks"}
                                </option>
                                <option value="etfs">
                                    {language === "ar"
                                        ? "صناديق الاستثمار"
                                        : "ETFs"}
                                </option>
                                <option value="indices">
                                    {language === "ar" ? "المؤشرات" : "Indices"}
                                </option>
                            </select>
                        </div>

                        <StandardFilterBar
                            selectedUnderlying={selectedUnderlying}
                            selectedExpiry={selectedExpiry}
                            selectedSide={selectedSide}
                            selectedMoneyness={selectedMoneyness}
                            selectedDataMode={selectedDataMode}
                            availableUnderlyings={underlyings}
                            availableExpiries={availableExpiries}
                            availableSides={availableSides}
                            availableMoneyness={availableMoneyness}
                            onUnderlyingChange={setSelectedUnderlying}
                            onExpiryChange={setSelectedExpiry}
                            onSideChange={setSelectedSide}
                            onMoneynessChange={setSelectedMoneyness}
                            onDataModeChange={(mode) => {
                                setSelectedDataMode(mode);
                                window.dispatchEvent(
                                    new CustomEvent("mmecDataModeChanged", {
                                        detail: { mode },
                                    }),
                                );
                            }}
                            showDataModeFilter={showDataModeFilter}
                            compact={false}
                        />

                        <div className="mt-4 flex items-center gap-4">
                            <label
                                className="text-sm font-medium"
                                style={{ color: colors.textSecondary }}
                            >
                                {translations.strikes}:
                            </label>
                            <select
                                value={selectedStrikes}
                                onChange={(e) =>
                                    setSelectedStrikes(
                                        e.target.value as
                                            | "10"
                                            | "20"
                                            | "30"
                                            | "all",
                                    )
                                }
                                style={{
                                    ...selectFieldStyle,
                                    width: "auto",
                                    minWidth: 140,
                                    fontSize: 13,
                                }}
                            >
                                <option value="all">
                                    {translations.strikesAll}
                                </option>
                                <option value="10">
                                    {translations.strikes10}
                                </option>
                                <option value="20">
                                    {translations.strikes20}
                                </option>
                                <option value="30">
                                    {translations.strikes30}
                                </option>
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {/* Summary Statistics */}
            {!hideSummaryStats && summaryStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div
                        className={`rounded-lg p-3 text-center${ds ? " vdr-tile" : ""}`}
                        style={{
                            background: colors.background,
                            border: `1px solid ${colors.surfaceBorder}`,
                        }}
                    >
                        <p
                            className="text-[10.5px] font-semibold uppercase tracking-[.08em]"
                            style={{ color: "var(--muted-2)" }}
                        >
                            {translations.boughtCalls}
                        </p>
                        <p
                            className="text-[20px] font-semibold mt-1"
                            style={{ color: colors.text, fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" }}
                        >
                            {summaryStats.totalBoughtCalls.toLocaleString()}
                        </p>
                    </div>
                    <div
                        className={`rounded-lg p-3 text-center${ds ? " vdr-tile" : ""}`}
                        style={{
                            background: colors.background,
                            border: `1px solid ${colors.surfaceBorder}`,
                        }}
                    >
                        <p
                            className="text-[10.5px] font-semibold uppercase tracking-[.08em]"
                            style={{ color: "var(--muted-2)" }}
                        >
                            {translations.soldCalls}
                        </p>
                        <p
                            className="text-[20px] font-semibold mt-1"
                            style={{ color: colors.text, fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" }}
                        >
                            {summaryStats.totalSoldCalls.toLocaleString()}
                        </p>
                    </div>
                    <div
                        className={`rounded-lg p-3 text-center${ds ? " vdr-tile" : ""}`}
                        style={{
                            background: colors.background,
                            border: `1px solid ${colors.surfaceBorder}`,
                        }}
                    >
                        <p
                            className="text-[10.5px] font-semibold uppercase tracking-[.08em]"
                            style={{ color: "var(--muted-2)" }}
                        >
                            {translations.bullish}
                        </p>
                        <p
                            className="text-[20px] font-semibold mt-1"
                            style={{ color: "var(--up-text, var(--up))", fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" }}
                        >
                            {summaryStats.bullishStrikes}
                        </p>
                    </div>
                    <div
                        className={`rounded-lg p-3 text-center${ds ? " vdr-tile" : ""}`}
                        style={{
                            background: colors.background,
                            border: `1px solid ${colors.surfaceBorder}`,
                        }}
                    >
                        <p
                            className="text-[10.5px] font-semibold uppercase tracking-[.08em]"
                            style={{ color: "var(--muted-2)" }}
                        >
                            {translations.bearish}
                        </p>
                        <p
                            className="text-[20px] font-semibold mt-1"
                            style={{ color: "var(--down-text, var(--down))", fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" }}
                        >
                            {summaryStats.bearishStrikes}
                        </p>
                    </div>
                </div>
            )}

            {/* Main Chart - Stacked Bars */}
            {selectedUnderlying === "all" && uniqueUnderlyings.length > 0 && (
                <div
                    className="text-center text-sm mb-2"
                    style={{ color: colors.text }}
                >
                    <span className="font-semibold">
                        {language === "ar" ? "الرمز الأساسي:" : "Underlying:"}
                    </span>{" "}
                    <span className="font-bold">
                        {uniqueUnderlyings.join(", ")}
                    </span>
                </div>
            )}
            <div
                className={`${fullScreen ? "h-[60vh]" : "h-[500px]"} w-full mb-4`}
            >
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={chartData}
                        margin={{ top: 12, right: 16, bottom: 8, left: 8 }}
                    >
                        {showGrid && (
                            <CartesianGrid
                                strokeDasharray="3 4"
                                stroke={ct.grid}
                            />
                        )}
                        <XAxis
                            dataKey="strike"
                            stroke={ct.border}
                            tick={{ fill: ct.text, fontSize: 11, fontFamily: "var(--font-mono)" }}
                            axisLine={{ stroke: ct.border }}
                            tickLine={false}
                        />
                        <YAxis
                            yAxisId="left"
                            stroke={ct.border}
                            tick={{ fill: ct.text, fontSize: 11, fontFamily: "var(--font-mono)" }}
                            axisLine={{ stroke: ct.border }}
                            tickLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} />

                        {/* Reformed stacked bars - all in one stack per strike */}
                        <Bar
                            yAxisId="left"
                            dataKey="boughtCalls"
                            stackId="volume"
                            fill={reformedColors.boughtCalls}
                            name={translations.boughtCalls}
                            onClick={onBarClick}
                            cursor="pointer"
                        />
                        <Bar
                            yAxisId="left"
                            dataKey="soldPuts"
                            stackId="volume"
                            fill={reformedColors.soldPuts}
                            name={translations.soldPuts}
                            onClick={onBarClick}
                            cursor="pointer"
                        />
                        <Bar
                            yAxisId="left"
                            dataKey="boughtPuts"
                            stackId="volume"
                            fill={reformedColors.boughtPuts}
                            name={translations.boughtPuts}
                            onClick={onBarClick}
                            cursor="pointer"
                        />
                        <Bar
                            yAxisId="left"
                            dataKey="soldCalls"
                            stackId="volume"
                            fill={reformedColors.soldCalls}
                            name={translations.soldCalls}
                            onClick={onBarClick}
                            cursor="pointer"
                        />

                        {/* Current Price Reference Line - Moved after bars for higher z-index */}
                        {currentPrice && (
                            <ReferenceLine
                                x={currentPrice.toString()}
                                stroke={priceLine}
                                strokeWidth={2}
                                strokeDasharray="3 4"
                                label={{
                                    value: `${translations.currentPrice}: ${currentPrice}`,
                                    position: "top",
                                    fill: colors.text,
                                    fontSize: 11,
                                }}
                            />
                        )}

                        {showLegend && <Legend content={<CustomLegend />} wrapperStyle={{ fontSize: 11, color: "var(--muted)" }} />}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            {/* Net Delta Pane - Taller */}
            <div className={`${fullScreen ? "h-[24vh]" : "h-[260px]"} w-full`}>
                <h3
                    className="text-[14px] font-semibold mb-2"
                    style={{ color: colors.text }}
                >
                    {translations.netDelta}
                </h3>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={chartData}
                        margin={{ top: 12, right: 16, bottom: 8, left: 8 }}
                    >
                        {showGrid && (
                            <CartesianGrid
                                strokeDasharray="3 4"
                                stroke={ct.grid}
                            />
                        )}
                        <XAxis
                            dataKey="strike"
                            stroke={ct.border}
                            tick={{ fill: ct.text, fontSize: 11, fontFamily: "var(--font-mono)" }}
                            axisLine={{ stroke: ct.border }}
                            tickLine={false}
                        />
                        <YAxis
                            stroke={ct.border}
                            tick={{ fill: ct.text, fontSize: 11, fontFamily: "var(--font-mono)" }}
                            axisLine={{ stroke: ct.border }}
                            tickLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} />

                        <ReferenceLine
                            y={0}
                            stroke={ct.border}
                            strokeDasharray="3 4"
                        />

                        <Bar
                            dataKey="netDelta"
                            fill={chartUp}
                            name={translations.netDelta}
                            onClick={onBarClick}
                            cursor="pointer"
                        >
                            {chartData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={
                                        entry.netDelta >= 0
                                            ? chartUp
                                            : chartDown
                                    }
                                />
                            ))}
                        </Bar>

                        {currentPrice && (
                            <ReferenceLine
                                x={currentPrice.toString()}
                                stroke={priceLine}
                                strokeWidth={2}
                                strokeDasharray="3 4"
                            />
                        )}

                        {showLegend && <Legend content={<CustomLegend />} wrapperStyle={{ fontSize: 11, color: "var(--muted)" }} />}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            {/* Net Premium Pane - New below Net Delta */}
            <div
                className={`${fullScreen ? "h-[20vh]" : "h-[220px]"} w-full mt-4`}
            >
                <h3
                    className="text-[14px] font-semibold mb-2"
                    style={{ color: colors.text }}
                >
                    {language === "ar" ? "صافي العلاوة" : "Net Premium"}
                </h3>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={chartData}
                        margin={{ top: 12, right: 16, bottom: 8, left: 8 }}
                    >
                        {showGrid && (
                            <CartesianGrid
                                strokeDasharray="3 4"
                                stroke={ct.grid}
                            />
                        )}
                        <XAxis
                            dataKey="strike"
                            stroke={ct.border}
                            tick={{ fill: ct.text, fontSize: 11, fontFamily: "var(--font-mono)" }}
                            axisLine={{ stroke: ct.border }}
                            tickLine={false}
                        />
                        <YAxis
                            stroke={ct.border}
                            tick={{ fill: ct.text, fontSize: 11, fontFamily: "var(--font-mono)" }}
                            axisLine={{ stroke: ct.border }}
                            tickLine={false}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <ReferenceLine
                            y={0}
                            stroke={ct.border}
                            strokeDasharray="3 4"
                        />
                        <Bar
                            dataKey="netPremium"
                            fill={chartUp}
                            name={
                                language === "ar"
                                    ? "صافي العلاوة"
                                    : "Net Premium"
                            }
                        >
                            {chartData.map((entry, index) => (
                                <Cell
                                    key={`cell-np-${index}`}
                                    fill={
                                        entry.netPremium >= 0
                                            ? chartUp
                                            : chartDown
                                    }
                                />
                            ))}
                        </Bar>
                        {showLegend && <Legend content={<CustomLegend />} wrapperStyle={{ fontSize: 11, color: "var(--muted)" }} />}

                        {currentPrice && (
                            <ReferenceLine
                                x={currentPrice.toString()}
                                stroke={priceLine}
                                strokeWidth={2}
                                strokeDasharray="3 4"
                            />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>

            {/* Debug Table */}
            <div className="mt-6">
                <details
                    className="rounded-lg p-4"
                    style={{
                        background: colors.background,
                        border: `1px solid ${colors.surfaceBorder}`,
                    }}
                >
                    <summary
                        className="cursor-pointer font-semibold mb-2"
                        style={{ color: colors.text }}
                    >
                        Debug Table - VolumeDeltaReformed Data
                    </summary>
                    <div className="overflow-x-auto">
                        <table className="w-full text-[12.5px]">
                            <thead>
                                <tr
                                    style={{
                                        borderBottom: `1px solid ${colors.surfaceBorder}`,
                                    }}
                                >
                                    <th
                                        className="text-end px-2.5 py-1.5 text-[11px] font-semibold tracking-[.03em]"
                                        style={{ color: "var(--muted)" }}
                                    >
                                        Strike
                                    </th>
                                    <th
                                        className="text-end px-2.5 py-1.5 text-[11px] font-semibold tracking-[.03em]"
                                        style={{ color: "var(--muted)" }}
                                    >
                                        {translations.boughtCalls}
                                    </th>
                                    <th
                                        className="text-end px-2.5 py-1.5 text-[11px] font-semibold tracking-[.03em]"
                                        style={{ color: "var(--muted)" }}
                                    >
                                        {translations.soldCalls}
                                    </th>
                                    <th
                                        className="text-end px-2.5 py-1.5 text-[11px] font-semibold tracking-[.03em]"
                                        style={{ color: "var(--muted)" }}
                                    >
                                        {translations.boughtPuts}
                                    </th>
                                    <th
                                        className="text-end px-2.5 py-1.5 text-[11px] font-semibold tracking-[.03em]"
                                        style={{ color: "var(--muted)" }}
                                    >
                                        {translations.soldPuts}
                                    </th>
                                    <th
                                        className="text-end px-2.5 py-1.5 text-[11px] font-semibold tracking-[.03em]"
                                        style={{ color: "var(--muted)" }}
                                    >
                                        {translations.netDelta}
                                    </th>
                                    <th
                                        className="text-end px-2.5 py-1.5 text-[11px] font-semibold tracking-[.03em]"
                                        style={{ color: "var(--muted)" }}
                                    >
                                        {language === "ar"
                                            ? "صافي العلاوة"
                                            : "Net Premium"}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {chartData.map((item, index) => (
                                    <tr
                                        key={index}
                                        style={{
                                            borderBottom: `1px solid ${colors.surfaceBorder}`,
                                        }}
                                    >
                                        <td
                                            className="text-end px-2.5 py-1.5 tabular-nums"
                                            style={{ color: colors.text }}
                                        >
                                            {item.strike}
                                        </td>
                                        <td
                                            className="text-end px-2.5 py-1.5 tabular-nums"
                                            style={{ color: colors.text }}
                                        >
                                            {item.boughtCalls.toFixed(0)}
                                        </td>
                                        <td
                                            className="text-end px-2.5 py-1.5 tabular-nums"
                                            style={{ color: colors.text }}
                                        >
                                            {item.soldCalls.toFixed(0)}
                                        </td>
                                        <td
                                            className="text-end px-2.5 py-1.5 tabular-nums"
                                            style={{ color: colors.text }}
                                        >
                                            {item.boughtPuts.toFixed(0)}
                                        </td>
                                        <td
                                            className="text-end px-2.5 py-1.5 tabular-nums"
                                            style={{ color: colors.text }}
                                        >
                                            {item.soldPuts.toFixed(0)}
                                        </td>
                                        <td
                                            className="text-end px-2.5 py-1.5 tabular-nums"
                                            style={{
                                                color:
                                                    item.netDelta > 0
                                                        ? colors.success
                                                        : item.netDelta < 0
                                                          ? colors.error
                                                          : colors.text,
                                            }}
                                        >
                                            {item.netDelta.toFixed(0)}
                                        </td>
                                        <td
                                            className="text-end px-2.5 py-1.5 tabular-nums"
                                            style={{ color: colors.text }}
                                        >
                                            {typeof item.netPremium === "number"
                                                ? item.netPremium.toFixed(0)
                                                : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </details>
            </div>

            <ToolExplanation
                title={translations.title}
                whatIsThis={
                    language === "ar"
                        ? "تحليل أحجام العرض/الطلب عبر سعر التنفيذ مع صافي الدلتا المطور."
                        : "Enhanced analysis of bid/ask volumes across strikes with net delta visualization."
                }
                howToUse={
                    language === "ar"
                        ? "حدد الأصل والانتهاء والجانب والنقودية، ثم راقب صافي الدلتا في اللوحة السفلية."
                        : "Set underlying, expiry, side, and moneyness, then monitor net delta in the bottom pane."
                }
                example={
                    language === "ar"
                        ? "إذا تفوق شراء خيارات الشراء وبيع خيار البيع على البيع حول 430 وكان صافي الدلتا إيجابي، يميل الاتجاه للصعود نحو 430."
                        : "If call buying and put selling dominate around 430 with positive net delta, bias is upward toward 430."
                }
            />
        </div>
    );
};

export default VolumeDeltaReformed;
